import React from 'react';
import { Pallet } from '../types';

interface PalletVisualizationProps {
  pallets: Pallet[];
}

export const PalletVisualization: React.FC<PalletVisualizationProps> = ({ pallets }) => {
  if (!pallets || pallets.length === 0) {
    return (
      <div className="bg-white p-6 rounded-lg shadow-md">
        <h2 className="text-xl font-bold mb-4">Pallet Visualization</h2>
        <p className="text-gray-600">No pallets to visualize. Run optimization first.</p>
      </div>
    );
  }

  // Calculate max pallet height for proportional scaling
  const maxPalletHeight = Math.max(...pallets.map(p => p.totalHeight));
  const minLayerPx = 18; // minimum px for a layer
  const maxLayerPx = 40; // maximum px for a layer
  const palletPx = 220; // total px for the tallest pallet

  return (
    <div className="bg-white p-6 rounded-lg shadow-md">
      <h2 className="text-xl font-bold mb-4">2D Pallet View</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {pallets.map((pallet, palletIndex) => (
          <div key={pallet.id} className="border rounded-lg p-4 bg-gray-50 flex flex-col items-center">
            <div className="flex items-center justify-between w-full mb-3">
              <h3 className="font-semibold text-lg">Pallet {palletIndex + 1}</h3>
              <span className="text-sm text-gray-600 ml-2 whitespace-nowrap">{pallet.totalHeight.toFixed(1)} cm</span>
            </div>
            {/* Vertical Pallet Stack */}
            <div className="relative flex flex-col items-center" style={{ height: palletPx, maxHeight: palletPx, minHeight: palletPx }}>
              {/* Pallet Base (bottom) */}
              <div className="absolute left-0 right-0 bottom-0 h-2 bg-gray-800 rounded" />
              {/* Pallet Layers (stacked from bottom up) */}
              <div className="flex flex-col-reverse justify-end w-32 sm:w-40 md:w-44 h-full z-10 overflow-y-auto" style={{maxHeight: palletPx}}>
                {pallet.layers.map((layer, layerIndex) => {
                  // Proportional height for each layer
                  const proportionalHeight = Math.max(
                    minLayerPx,
                    Math.min(maxLayerPx, (layer.height / maxPalletHeight) * palletPx)
                  );
                  return (
                    <div
                      key={layerIndex}
                      className="bg-gray-200 border border-gray-300 rounded mb-1 flex items-center px-2"
                      style={{ height: proportionalHeight, minHeight: minLayerPx, maxHeight: maxLayerPx }}
                      title={`Layer ${layerIndex + 1}: ${layer.boxes.length} boxes (${layer.height} cm)`}
                    >
                      <span className="text-xs font-medium text-gray-700 flex-1 truncate" style={{textAlign: 'left'}}>
                        {layer.boxes.length} boxes
                      </span>
                      <span className="text-xs font-mono text-gray-600 flex-1 truncate text-right" style={{textAlign: 'right'}}>
                        {layer.boxes[0]?.asin || ''}
                      </span>
                    </div>
                  );
                })}
              </div>
              {/* Pallet Top (top) */}
              <div className="absolute left-0 right-0 top-0 h-2 bg-gray-800 rounded" />
            </div>
            {/* Product IDs for this pallet */}
            {pallet.asins.length > 0 && (
              <div className="text-center mt-2">
                <div className="flex flex-wrap justify-center gap-1">
                  {pallet.asins.map((asin, asinIndex) => (
                    <span
                      key={asinIndex}
                      className="px-2 py-1 bg-gray-200 text-gray-700 text-xs rounded-full font-mono"
                    >
                      {asin}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}; 